import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useUser } from "@/contexts/UserContext";
import { usePushNotifications, showLocalNotification } from "@/hooks/usePushNotifications";

interface Profile { id: string; username: string; display_name: string | null; avatar_url: string | null; }
interface Message { id: string; sender_id: string; receiver_id: string; content: string; created_at: string; read_at: string | null; }
interface Conversation { profile: Profile; lastMessage: Message; unreadCount: number; }

const SUPABASE_FUNCTIONS_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1`;

export default function MessagesSection() {
  const { user } = useUser();
  const { permission, isSupported, requestPermission, subscribing } = usePushNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConv, setActiveConv] = useState<Profile | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [searchUser, setSearchUser] = useState("");
  const [searchResults, setSearchResults] = useState<Profile[]>([]);
  const [searching, setSearching] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const activeConvRef = useRef<Profile | null>(null);

  // ─── Request notification permission banner ──────────────────────────────────
  const [showPermBanner, setShowPermBanner] = useState(false);

  useEffect(() => {
    if (isSupported && permission === "default") {
      const t = setTimeout(() => setShowPermBanner(true), 1200);
      return () => clearTimeout(t);
    } else {
      setShowPermBanner(false);
    }
  }, [isSupported, permission]);

  const handleEnableNotifs = async () => {
    const granted = await requestPermission();
    if (granted) setShowPermBanner(false);
  };

  // Keep ref in sync
  useEffect(() => { activeConvRef.current = activeConv; }, [activeConv]);

  // ─── Fetch conversations ─────────────────────────────────────────────────────
  const fetchConversations = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("messages")
      .select("id, sender_id, receiver_id, content, created_at, read_at")
      .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
      .order("created_at", { ascending: false });

    if (!data) { setLoadingConvs(false); return; }

    const partnerMap = new Map<string, { msgs: Message[]; unread: number }>();
    (data as Message[]).forEach((m) => {
      const partnerId = m.sender_id === user.id ? m.receiver_id : m.sender_id;
      if (!partnerMap.has(partnerId)) partnerMap.set(partnerId, { msgs: [], unread: 0 });
      const entry = partnerMap.get(partnerId)!;
      if (entry.msgs.length === 0) entry.msgs.push(m);
      if (m.receiver_id === user.id && !m.read_at) entry.unread++;
    });

    const partnerIds = Array.from(partnerMap.keys());
    if (partnerIds.length === 0) { setConversations([]); setLoadingConvs(false); return; }

    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .in("id", partnerIds);

    const convs: Conversation[] = (profiles ?? []).map((p) => {
      const entry = partnerMap.get(p.id)!;
      return { profile: p as Profile, lastMessage: entry.msgs[0], unreadCount: entry.unread };
    }).sort((a, b) => new Date(b.lastMessage.created_at).getTime() - new Date(a.lastMessage.created_at).getTime());

    setConversations(convs);
    setLoadingConvs(false);
  }, [user]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // ─── Fetch messages for active conversation ──────────────────────────────────
  const fetchMessages = useCallback(async (partnerId: string) => {
    if (!user) return;
    const { data } = await supabase
      .from("messages")
      .select("id, sender_id, receiver_id, content, created_at, read_at")
      .or(
        `and(sender_id.eq.${user.id},receiver_id.eq.${partnerId}),and(sender_id.eq.${partnerId},receiver_id.eq.${user.id})`
      )
      .order("created_at", { ascending: true });
    setMessages((data ?? []) as Message[]);
  }, [user]);

  // ─── Realtime subscriptions ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    // Handler: runs on any INSERT involving this user
    const handleNewMessage = async (payload: { new: Record<string, unknown> }) => {
      const newMsg = payload.new as Message;
      const currentConv = activeConvRef.current;

      // Determine if this message belongs to the currently open conversation
      const belongsToActive = currentConv && (
        (newMsg.sender_id === currentConv.id && newMsg.receiver_id === user.id) ||
        (newMsg.sender_id === user.id && newMsg.receiver_id === currentConv.id)
      );

      if (belongsToActive) {
        // Re-fetch messages to ensure we have the complete, accurate list
        await fetchMessages(currentConv!.id);

        // Mark incoming messages as read
        if (newMsg.receiver_id === user.id && !newMsg.read_at) {
          await supabase
            .from("messages")
            .update({ read_at: new Date().toISOString() })
            .eq("id", newMsg.id);
        }
      } else if (newMsg.receiver_id === user.id) {
        // Message from someone NOT in the active conversation — show local notification
        const senderProfile = conversations.find((c) => c.profile.id === newMsg.sender_id)?.profile;
        const senderName = senderProfile?.display_name || senderProfile?.username || "Birisi";
        showLocalNotification(
          `${senderName} size mesaj gönderdi`,
          newMsg.content.slice(0, 100),
          `/account?tab=messages&user=${senderProfile?.username ?? ""}`,
        );
      }

      // Always refresh conversation list (sidebar counts + last message)
      fetchConversations();
    };

    // Subscription 1: messages where I am the RECEIVER
    const receiverChannel = supabase
      .channel(`msgs_recv:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `receiver_id=eq.${user.id}`,
        },
        handleNewMessage
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "messages",
          filter: `receiver_id=eq.${user.id}`,
        },
        () => { fetchConversations(); }
      )
      .subscribe();

    // Subscription 2: messages where I am the SENDER
    // (handles the case where the sender sees their own sent message echoed back)
    const senderChannel = supabase
      .channel(`msgs_sent:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `sender_id=eq.${user.id}`,
        },
        async (payload) => {
          const newMsg = payload.new as Message;
          const currentConv = activeConvRef.current;
          if (currentConv && newMsg.receiver_id === currentConv.id) {
            // Refresh to sync with DB (avoids duplicates from optimistic updates)
            await fetchMessages(currentConv.id);
          }
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(receiverChannel);
      supabase.removeChannel(senderChannel);
    };
  }, [user, fetchConversations, fetchMessages, conversations]);

  // ─── Open conversation from URL param ───────────────────────────────────────
  useEffect(() => {
    const paramUser = searchParams.get("user");
    if (!paramUser || !user) return;
    supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .eq("username", paramUser)
      .maybeSingle()
      .then(({ data }) => {
        if (data) { setActiveConv(data as Profile); setSearchParams({}); }
      });
  }, [searchParams, user, setSearchParams]);

  // ─── Load messages when conversation opens ───────────────────────────────────
  useEffect(() => {
    if (!activeConv || !user) return;
    fetchMessages(activeConv.id).then(() => {
      // Mark incoming as read
      supabase
        .from("messages")
        .update({ read_at: new Date().toISOString() })
        .eq("sender_id", activeConv.id)
        .eq("receiver_id", user.id)
        .is("read_at", null)
        .then(() => { fetchConversations(); });
    });
  }, [activeConv, user, fetchMessages, fetchConversations]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ─── Send message ────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!input.trim() || !activeConv || !user || sending) return;
    setSending(true);
    const content = input.trim();
    setInput("");

    // Optimistic update — add immediately to UI
    const tempId = `temp_${Date.now()}`;
    const optimisticMsg: Message = {
      id: tempId,
      sender_id: user.id,
      receiver_id: activeConv.id,
      content,
      created_at: new Date().toISOString(),
      read_at: null,
    };
    setMessages((prev) => [...prev, optimisticMsg]);

    const { error } = await supabase
      .from("messages")
      .insert({ sender_id: user.id, receiver_id: activeConv.id, content });

    setSending(false);

    if (error) {
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    } else {
      // Trigger Web Push notification to receiver (for closed-tab delivery)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          fetch(`${SUPABASE_FUNCTIONS_URL}/send-push`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              receiver_id: activeConv.id,
              title: `${user.displayName || user.username} size mesaj gönderdi`,
              body: content.slice(0, 120),
              url: `/account?tab=messages&user=${user.username}`,
            }),
          }).catch(() => {});
        }
      } catch {
        // push is optional, never block the send
      }
    }
  };

  // ─── Search users ────────────────────────────────────────────────────────────
  const handleSearch = async (q: string) => {
    setSearchUser(q);
    if (q.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const { data } = await supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .ilike("username", `%${q.trim()}%`)
      .neq("id", user?.id ?? "")
      .limit(5);
    setSearchResults((data ?? []) as Profile[]);
    setSearching(false);
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const avatarEl = (p: Profile, size = "w-10 h-10") => {
    const init = (p.display_name || p.username).charAt(0).toUpperCase();
    return p.avatar_url
      ? <img src={p.avatar_url} alt={p.username} className={`${size} rounded-full object-cover object-top flex-shrink-0`} />
      : (
        <div className={`${size} rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center flex-shrink-0`}>
          <span className="text-white font-bold text-sm">{init}</span>
        </div>
      );
  };

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden flex flex-col" style={{ minHeight: 600 }}>
      {/* Notification permission banner */}
      {showPermBanner && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border-b border-amber-100">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
            <i className="ri-notification-3-line text-amber-600 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">Mesaj bildirimleri</p>
            <p className="text-xs text-amber-600">Sekme kapalıyken bile yeni mesajlardan haberdar olun.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleEnableNotifs}
              disabled={subscribing}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
            >
              {subscribing ? <i className="ri-loader-4-line animate-spin" /> : "Etkinleştir"}
            </button>
            <button
              onClick={() => setShowPermBanner(false)}
              className="w-6 h-6 flex items-center justify-center text-amber-500 hover:text-amber-700 cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* ── Sidebar ────────────────────────────────────────────────────────── */}
        <div className="w-64 md:w-72 flex-shrink-0 border-r border-stone-100 flex flex-col">
          <div className="p-4 border-b border-stone-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-stone-900 text-base">Mesajlar</h3>
              {totalUnread > 0 && (
                <span className="px-2 py-0.5 bg-emerald-600 text-white text-xs font-bold rounded-full">
                  {totalUnread} yeni
                </span>
              )}
            </div>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
              <input
                value={searchUser}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Kullanıcı ara..."
                className="w-full pl-8 pr-3 py-2 bg-stone-50 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 transition-colors"
              />
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 bg-white border border-stone-200 rounded-xl overflow-hidden">
                {searchResults.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { setActiveConv(p); setSearchUser(""); setSearchResults([]); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-stone-50 cursor-pointer transition-colors text-left"
                  >
                    {avatarEl(p, "w-8 h-8")}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">{p.display_name || p.username}</p>
                      <p className="text-xs text-stone-400">@{p.username}</p>
                    </div>
                  </button>
                ))}
                {searching && (
                  <div className="p-2 text-center"><i className="ri-loader-4-line animate-spin text-stone-400" /></div>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingConvs ? (
              <div className="flex justify-center p-6">
                <i className="ri-loader-4-line animate-spin text-stone-400 text-xl" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center p-6 text-stone-400 text-sm">
                Henüz mesaj yok.<br />Kullanıcı ara ve mesaj gönder.
              </div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.profile.id}
                  onClick={() => setActiveConv(conv.profile)}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 cursor-pointer transition-colors text-left border-b border-stone-50 ${
                    activeConv?.id === conv.profile.id ? "bg-emerald-50" : ""
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    {avatarEl(conv.profile)}
                    {conv.unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-emerald-600 text-white text-xs rounded-full flex items-center justify-center px-1 font-bold">
                        {conv.unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-1">
                      <p className={`text-sm truncate ${conv.unreadCount > 0 ? "font-bold text-stone-900" : "font-semibold text-stone-700"}`}>
                        {conv.profile.display_name || conv.profile.username}
                      </p>
                      <p className="text-xs text-stone-400 flex-shrink-0">
                        {new Date(conv.lastMessage.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                    <p className={`text-xs truncate mt-0.5 ${conv.unreadCount > 0 ? "text-stone-700 font-medium" : "text-stone-400"}`}>
                      {conv.lastMessage.sender_id === user?.id ? "Sen: " : ""}{conv.lastMessage.content}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ── Chat Area ──────────────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0">
          {!activeConv ? (
            <div className="flex-1 flex items-center justify-center flex-col gap-3 text-stone-400">
              <div className="w-16 h-16 flex items-center justify-center bg-stone-50 rounded-full">
                <i className="ri-message-3-line text-4xl text-stone-300" />
              </div>
              <p className="text-sm">Bir konuşma seçin veya kullanıcı arayın</p>
              {totalUnread > 0 && (
                <p className="text-xs text-emerald-600 font-medium">{totalUnread} okunmamış mesaj</p>
              )}
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-100 bg-white">
                {avatarEl(activeConv, "w-9 h-9")}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-stone-900 text-sm">{activeConv.display_name || activeConv.username}</p>
                  <Link
                    to={`/profile/${activeConv.username}`}
                    className="text-xs text-stone-400 hover:text-emerald-600 cursor-pointer"
                  >
                    @{activeConv.username}
                  </Link>
                </div>
                {/* Realtime indicator */}
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 rounded-full mr-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs text-emerald-600 font-medium">Canlı</span>
                </div>
                <button
                  onClick={() => setActiveConv(null)}
                  className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer rounded-lg hover:bg-stone-100 transition-colors"
                >
                  <i className="ri-close-line" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-stone-50/40">
                {messages.length === 0 && (
                  <div className="text-center text-stone-400 text-sm pt-8">İlk mesajı gönder!</div>
                )}
                {messages.map((msg) => {
                  const isMine = msg.sender_id === user?.id;
                  const isOptimistic = msg.id.startsWith("temp_");
                  return (
                    <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-xs px-4 py-2.5 rounded-2xl text-sm ${
                          isMine
                            ? "bg-emerald-700 text-white rounded-br-sm"
                            : "bg-white text-stone-800 rounded-bl-sm border border-stone-200"
                        } ${isOptimistic ? "opacity-60" : ""}`}
                      >
                        <p className="leading-relaxed">{msg.content}</p>
                        <div className={`flex items-center gap-1 mt-1 ${isMine ? "justify-end" : ""}`}>
                          <p className={`text-xs ${isMine ? "text-emerald-200" : "text-stone-400"}`}>
                            {new Date(msg.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                          </p>
                          {isMine && (
                            isOptimistic
                              ? <i className="ri-time-line text-xs text-emerald-300" title="Gönderiliyor..." />
                              : msg.read_at
                                ? <i className="ri-check-double-line text-xs text-emerald-200" title="Okundu" />
                                : <i className="ri-check-line text-xs text-emerald-300" title="Gönderildi" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="px-4 py-3 border-t border-stone-100 bg-white flex gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                  }}
                  placeholder="Mesaj yaz..."
                  className="flex-1 bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || sending}
                  className="w-10 h-10 flex items-center justify-center bg-emerald-700 text-white rounded-xl cursor-pointer hover:bg-emerald-800 disabled:opacity-50 transition-colors flex-shrink-0"
                >
                  {sending
                    ? <i className="ri-loader-4-line animate-spin text-sm" />
                    : <i className="ri-send-plane-fill text-sm" />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
